import { COMMA, EMPTY_STRING } from '../../../NonReact/constants/Common'
import { SERVLET_COMMAND, ServletCmds } from '../../../NonReact/constants/ServletCommands'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { updateReportConfiguration } from '../../ConfigureReport/API'
import {
    mapConfiguredDashboardReportsToStatisticsDisplayString,
    mapDashboardLayoutStyleToStatisticsSettingsString,
    mapDaysStatePropertyToDaysOfWeekBitmap,
    mapFinancialDecimalSeparatorToStringValue,
    mapFinancialGroupingSeparatorToStringValue,
    mapResponseDaysOfTheWeekToDaysOfWeekArray,
    mapResponseFilterCallChannelsBitmapToChannelTypes,
    mapStateChannelTypesValuesToRequestFilterChannels
} from '../../ConfigureReport/ApiMapper'
import { DashboardLayoutStyle, ReportListItemType } from '../../ConfigureReport/DashboardTab/Types'
import { FOLDERS_SEPARATOR_BACKEND, FOLDERS_SEPARATOR_DISPLAY } from '../../MainMenu/ReportList/Constants'
import { fetchReportList } from '../../../NonReact/common/RxJS/ReportListState'
import { servletPath } from '../../../NonReact/common/RxJS/RxJS'
import { RptConfigurationDataResponseDto, RptConfigurationDataUpdateDto } from '../Types/Types'
import { arraySwap } from '../Utils/Array'
import { prepareReportDescriptionForAddUpdateRequest } from '../Utils/Reports'
import {
    convertDateToDurationDateTimeString,
    convertDateToDurationTimeString,
    convertDurationInSecondsToFormattedString,
    convertDurationTimeStringToDate
} from '../Utils/TimeFormats'
import { requestGetJSON, RESTAPIError } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { displayError } from '../Utils/Messages'

type RequestResult = {
    Error: string
    ResultData: RptConfigurationDataResponseDto
}

type RequestResults = {
    RequestResult: RequestResult
}

type ReportResponse = {
    RequestResults?: RequestResults[]
}

/**
 * Get the report from the REST endpoint.
 *
 * @param reportID - The ID of the report to get.
 *
 * @returns - A promise of the rpt configuration response DTO.
 */
export const getReport = async (reportID: string): Promise<RptConfigurationDataResponseDto> =>
    // Do the REST request with a cache of 1 second.
    requestGetJSON<ReportResponse>(
        URLConstructor.newInstance({
            pathParams: [servletPath],
            queryParams: {
                [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
                InvokeID: `UI-${Date.now()}`,
                Action: 'Get',
                ResType: 'JSON',
                ID: reportID,
                IncludeScheduleData: 'true'
            }
        }),
        { cacheTime: 1000 }
    ).then((res: Nullable<ReportResponse>): RptConfigurationDataResponseDto => {
        // Assume the result is a ReportResponse.
        const requestResults: Definable<RequestResults[]> = res?.RequestResults

        if (requestResults && Array.isArray(requestResults) && requestResults.length > 0) {
            const result: RequestResult = requestResults[0].RequestResult

            if (IsStringEmpty(result.Error)) {
                return result.ResultData
            }

            // There was an error defined by the server.
            throw new RESTAPIError(0, null, result.Error, null)
        }

        // There was an error in the request.
        throw new RESTAPIError(0, null, 'There was an error getting the report data', null)
    })

/**
 * Update part of the result.
 *
 * @param reportID - ID to modify.
 * @param language - The language object.
 * @param func - Function to perform to update the result data.
 *
 * @returns - Promise of the updated configuration data.
 */
const updateStatsDisplay = async (
    reportID: string,
    language: CLanguageHandler,
    func: (res: RptConfigurationDataResponseDto) => void
): Promise<RptConfigurationDataResponseDto> =>
    new Promise((resolve, reject) => {
        getReport(reportID).then((res: RptConfigurationDataResponseDto): void => {
            if (res && !IsVarEmpty(res.StatisticsDisplay)) {
                func(res)

                const request: RptConfigurationDataUpdateDto = {
                    ...res,
                    FilterCallChannels: mapStateChannelTypesValuesToRequestFilterChannels(
                        mapResponseFilterCallChannelsBitmapToChannelTypes(res.FilterCallChannels)
                    ),
                    AutoSwitchIntervalDisplayText: convertDurationInSecondsToFormattedString(
                        res.AutoSwitchIntervalDisplay / 1000,
                        language
                    ),
                    DurationFilterSummaryText: undefined
                } as unknown as RptConfigurationDataUpdateDto

                updateReportConfiguration(servletPath, request)
                    .then(resolve)
                    .catch(reject)
                    .then(() => fetchReportList(reportID, res.Description).catch(displayError))
            }
        })
    })

export const updateReportDescription = async (
    reportID: string,
    folderPath: string,
    language: CLanguageHandler
): Promise<RptConfigurationDataResponseDto> =>
    new Promise((resolve, reject) => {
        getReport(reportID)
            .then((res: RptConfigurationDataResponseDto): void => {
                if (res) {
                    const request: RptConfigurationDataUpdateDto = {
                        ...res,
                        FilterCallChannels: mapStateChannelTypesValuesToRequestFilterChannels(
                            mapResponseFilterCallChannelsBitmapToChannelTypes(res.FilterCallChannels)
                        ),
                        AutoSwitchIntervalDisplayText: convertDurationInSecondsToFormattedString(
                            res.AutoSwitchIntervalDisplay / 1000,
                            language
                        ),
                        DurationFilterSummaryText: undefined,
                        StatisticSettingFinancialCurrencyDecimalSeparator: mapFinancialDecimalSeparatorToStringValue(
                            res.StatisticSettingFinancialCurrencyDecimalSeparator
                        ),
                        StatisticSettingFinancialCurrencyGroupingSeparator: mapFinancialGroupingSeparatorToStringValue(
                            res.StatisticSettingFinancialCurrencyGroupingSeparator
                        ),
                        DurationCustomStart: convertDateToDurationDateTimeString(
                            new Date(res.DurationCustomStart),
                            language
                        ),
                        DurationCustomEnd: convertDateToDurationDateTimeString(
                            new Date(res.DurationCustomEnd),
                            language
                        ),
                        DurationTimeOfDayStart: convertDateToDurationTimeString(
                            convertDurationTimeStringToDate(res.DurationTimeOfDayStart)
                        ),
                        DurationTimeOfDayEnd: convertDateToDurationTimeString(
                            convertDurationTimeStringToDate(res.DurationTimeOfDayEnd)
                        ),
                        DurationDaysOfWeek: mapDaysStatePropertyToDaysOfWeekBitmap(
                            mapResponseDaysOfTheWeekToDaysOfWeekArray(res.DurationDaysOfWeek, language),
                            language
                        ),
                        Description: folderPath
                    } as unknown as RptConfigurationDataUpdateDto

                    updateReportConfiguration(servletPath, request)
                        .then(resolve)
                        .catch(reject)
                        .then((): Promise<void> => fetchReportList(reportID, res.Description).catch(displayError))
                }
            })
            .catch(reject)
    })

/**
 * It takes a list of reports, a new folder path, and the current folder name, and updates the report
 * descriptions of all the reports in the list
 * @param {ReportListItemType[]} reports - ReportListItemType[] - an array of reports to be updated
 * @param {string} newFolderPath - the new folder path
 * @param {string} currentFolderName - the name of the folder that is being moved
 * @param {string} baseFolderPath - the base folder path (folder currently being moved)
 */
export const updateReportDescriptions = async (
    reports: ReportListItemType[],
    newFolderPath: string,
    currentFolderName: string,
    baseFolderPath: string,
    language: CLanguageHandler
): Promise<void> => {
    const splitBaseFolderPath: string[] = baseFolderPath.split(FOLDERS_SEPARATOR_BACKEND)

    return new Promise((resolve, reject): void => {
        reports.forEach((report: ReportListItemType): void => {
            const splitParentFolderId: string[] = report?.parentFolderID?.split(FOLDERS_SEPARATOR_BACKEND) || []

            // if the base folder is being dragged from the top level container then we need to account for this my removing an extra element
            const removeParentFromNewPathIndexID: number =
                splitBaseFolderPath.length <= 1 ? splitBaseFolderPath.length - 1 : splitBaseFolderPath.length

            const parentFolderIDIndex: number = report?.parentFolderID
                ? splitParentFolderId.slice(removeParentFromNewPathIndexID).indexOf(currentFolderName)
                : 0

            const newParentFolderID: string =
                splitParentFolderId
                    .slice(removeParentFromNewPathIndexID)
                    .slice(parentFolderIDIndex, splitParentFolderId.length)
                    .join(FOLDERS_SEPARATOR_DISPLAY) || EMPTY_STRING

            updateReportDescription(
                report.id,
                prepareReportDescriptionForAddUpdateRequest(`${newFolderPath}\\${newParentFolderID}`, report.name),
                language
            ).catch(reject)
        })
    })
}

/**
 * Change the layout setting for the dashboard.
 *
 * @param reportID - ID for the report to change.
 * @param language - The language object.
 * @param layout - The layout to change to.
 *
 * @returns - Updated report.
 */
export const modifyDisplaySettingsDashboard = async (
    reportID: Definable<string>,
    language: CLanguageHandler,
    layout: DashboardLayoutStyle
): Promise<RptConfigurationDataResponseDto> => {
    if (!IsStringEmpty(reportID)) {
        return updateStatsDisplay(reportID, language, (res: RptConfigurationDataResponseDto): void => {
            // Update sub report list.
            res.StatisticsDisplay = mapConfiguredDashboardReportsToStatisticsDisplayString(
                res.StatisticsDisplay.split(COMMA),
                layout
            )
            // Update the layout.
            res.StatisticsSettings = mapDashboardLayoutStyleToStatisticsSettingsString(layout)
        })
    }

    return Promise.reject()
}

/**
 * Modify a single dashboard update.
 *
 * @param reportID - ID of the report to update.
 * @param index - Index to update.
 * @param newID - ID to update to.
 * @param language - The language object.
 *
 * @returns - Updated report data.
 */
export const modifyDashboardSubReports = async (
    reportID: string,
    index: number,
    newID: string,
    language: CLanguageHandler
): Promise<RptConfigurationDataResponseDto> =>
    updateStatsDisplay(reportID, language, (res: RptConfigurationDataResponseDto): void => {
        const statsDisplay: string[] = res.StatisticsDisplay.split(COMMA)

        if (statsDisplay && statsDisplay.length > 0) {
            // If the report is used in a dashboard sub report that isn't in view, remove it from that location.
            statsDisplay.map((statDisplay: string): string => {
                if (statDisplay === newID) {
                    return ''
                }

                return statDisplay
            })
            statsDisplay[index] = newID
            res.StatisticsDisplay = statsDisplay.join(COMMA)
        }
    })

/**
 * Swap two dashboard sub reports.
 *
 * @param reportID - The ID of the report to update.
 * @param indexA - First index of the swap.
 * @param indexB - Second index of the swap.
 * @param language - Language object.
 *
 * @returns - Updated response.
 */
export const switchDashboardSubReports = async (
    reportID: string,
    indexA: number,
    indexB: number,
    language: CLanguageHandler
): Promise<RptConfigurationDataResponseDto> =>
    updateStatsDisplay(reportID, language, (res: RptConfigurationDataResponseDto): void => {
        // Split the string into an array.
        const statsDisplay: string[] = res.StatisticsDisplay.split(COMMA)

        if (statsDisplay && statsDisplay.length > 0) {
            // Swap the array items.
            arraySwap(statsDisplay, indexA, indexB)
            res.StatisticsDisplay = statsDisplay.join(COMMA)
        }
    })
